import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { FaClipboardList, FaCheck, FaTimes, FaEye } from "react-icons/fa";
import moment from "moment";
import styles from "./List.module.css";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import { fetchStaffClassAndSubjects } from "../../../../redux/Masterdata";
import { CustomButton, CustomDropdown } from "../../../../components";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";

const PublishMarkList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const staff_id = clientData?.master_id || "";
  const school_id = clientData?.school_id || "";
  const academic_year_id = clientData?.academic_year_id || "";

  const { translations } = useSelector((state) => state.language);
  const t = useMemo(() => translations?.labels || {}, [translations]);

  // Redux masterData
  const {
    staffClassAndSubjectOptions: reduxClassOptions,
    staffClassAndSubjectLoading: classLoading,
  } = useSelector((state) => state.masterData);

  // ─── Filter state ───
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedExamName, setSelectedExamName] = useState("");

  // ─── Class dropdown (deduplicated by class_id) ───
  const classOptions = useMemo(() => {
    const seen = new Set();
    return (reduxClassOptions || [])
      .filter((item) => {
        const key = String(item.class_id);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((item) => ({
        value: String(item.class_id),
        label: item.class_name,
      }));
  }, [reduxClassOptions]);

  // ─── Section dropdown (filtered by selected class_id, deduplicated) ───
  const sectionOptions = useMemo(() => {
    if (!selectedClass) return [];
    const classId =
      typeof selectedClass === "object" ? selectedClass.value : selectedClass;
    const seen = new Set();
    return (reduxClassOptions || [])
      .filter((item) => {
        if (String(item.class_id) !== String(classId)) return false;
        const key = String(item.section_id);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((item) => ({
        value: String(item.section_id),
        label: String(item.section_id),
      }));
  }, [reduxClassOptions, selectedClass]);

  // ─── Exam Name dropdown ───
  const [examNameOptions, setExamNameOptions] = useState([]);

  const fetchExamNames = useCallback(async () => {
    try {
      const payload = {
        school_id: parseInt(school_id),
        academic_year: parseInt(academic_year_id),
      };
      const response = await AxiosInstance.post(
        "/exam/examConfig/list",
        payload,
      );
      if (response?.data?.status) {
        const examData = response.data.data || [];
        const options = examData.map((item) => ({
          value: String(item.id),
          label: item.exam_type || item.exam_name,
        }));
        setExamNameOptions(options);
      }
    } catch (error) {
      console.error("Error fetching exam names:", error);
      setExamNameOptions([]);
    }
  }, [school_id, academic_year_id]);

  // ─── Table state ───
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  // ─── Fetch staff class & subjects on mount ───
  useEffect(() => {
    if (staff_id) {
      dispatch(
        fetchStaffClassAndSubjects({
          staff_id: parseInt(staff_id),
          class_id: "",
          section_id: "",
          subject_id: "",
        }),
      );
    }
  }, [dispatch, staff_id]);

  // ─── Fetch exam names on mount ───
  useEffect(() => {
    fetchExamNames();
  }, [fetchExamNames]);

  // ─── Fetch publish list API ───
  const fetchPublishList = async () => {
    if (!selectedClass || !selectedSection || !selectedExamName) return;

    const classId =
      typeof selectedClass === "object" ? selectedClass.value : selectedClass;
    const sectionId =
      typeof selectedSection === "object"
        ? selectedSection.value
        : selectedSection;
    const examId =
      typeof selectedExamName === "object"
        ? selectedExamName.value
        : selectedExamName;

    setSearchLoading(true);
    setLoading(true);
    try {
      const payload = {
        school_id: parseInt(school_id),
        staff_id: parseInt(staff_id),
        class_id: parseInt(classId),
        section_id: sectionId,
        config_id: parseInt(examId),
      };

      const response = await AxiosInstance.post(
        "/exam/examPublish/list",
        payload,
      );

      if (response?.data?.status) {
        const publishData = response.data.data || [];
        const transformedData = publishData.map((item, index) => ({
          id: index + 1,
          sno: index + 1,
          class_id: item.class_id,
          class_name: item.class_name || "",
          section_id: item.section_id,
          publish_marks: item.publish_marks || false,
          exam_name: item.exam_name || "",
          from_date: item.start_date
            ? moment(item.start_date).format("DD/MM/YYYY")
            : "",
          to_date: item.end_date
            ? moment(item.end_date).format("DD/MM/YYYY")
            : "",
          subjects: item.subject || [],
          config_id: parseInt(examId),
        }));
        setData(transformedData);
        setRowCount(transformedData.length);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error fetching publish list:", error);
      setData([]);
      setRowCount(0);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  // ─── Filter handlers ───
  const handleClassChange = (value) => {
    setSelectedClass(value);
    setSelectedSection("");
    setData([]);
  };

  const handleSectionChange = (value) => {
    setSelectedSection(value);
    setData([]);
  };

  const handleExamNameChange = (value) => {
    setSelectedExamName(value);
    setData([]);
  };

  const handleSearch = () => {
    if (!selectedClass || !selectedSection || !selectedExamName) return;
    fetchPublishList();
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const handleReset = () => {
    setSelectedClass("");
    setSelectedSection("");
    setSelectedExamName("");
    setData([]);
    setRowCount(0);
    setApiSearch("");
    setSearchInput("");
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const handlePublishView = (row) => {
    const subjects = row.subjects || [];
    const allVerified =
      subjects.length > 0 && subjects.every((s) => s.is_verify === 0);

    navigate("/user/publishView", {
      state: {
        class_id: row.class_id,
        section_id: row.section_id,
        config_id: row.config_id,
        exam_name: row.exam_name,
        class_name: row.class_name,
        data: row,
        allVerified,
      },
    });
  };

  // ─── Filter + Paginate ───
  const filteredData = useMemo(() => {
    if (!apiSearch) return data;
    const searchLower = apiSearch.toLowerCase();
    return data.filter(
      (item) =>
        item.class_name?.toLowerCase().includes(searchLower) ||
        item.exam_name?.toLowerCase().includes(searchLower) ||
        String(item.section_id)?.toLowerCase().includes(searchLower) ||
        item.from_date?.toLowerCase().includes(searchLower) ||
        item.to_date?.toLowerCase().includes(searchLower),
    );
  }, [data, apiSearch]);

  const paginatedData = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize;
    const end = start + pagination.pageSize;
    return filteredData.slice(start, end);
  }, [filteredData, pagination.pageIndex, pagination.pageSize]);

  // ─── Table columns ───
  const columns = useMemo(
    () => [
      {
        accessorKey: "class_name",
        header: t.class || "Class",
        enableSorting: true,
        enableColumnFilter: false,
        size: 80,
      },
      {
        accessorKey: "section_id",
        header: t.section || "Section",
        enableSorting: true,
        enableColumnFilter: false,
        size: 80,
      },
      {
        accessorKey: "exam_name",
        header: t.examName || "Exam Name",
        enableSorting: true,
        enableColumnFilter: false,
        size: 140,
      },
      {
        accessorKey: "from_date",
        header: t.fromDate || "From Date",
        enableSorting: true,
        enableColumnFilter: false,
        size: 120,
      },
      {
        accessorKey: "to_date",
        header: t.toDate || "To Date",
        enableSorting: true,
        enableColumnFilter: false,
        size: 120,
      },
      {
        accessorKey: "subjects",
        header: t.subjects || "Subjects",
        enableSorting: false,
        enableColumnFilter: false,
        size: 250,
        Cell: ({ row }) => {
          const subjects = row.original.subjects || [];
          return (
            <div className={styles.subjectsList}>
              {subjects.map((sub) => (
                <div key={sub.subject_id} className={styles.subjectRow}>
                  <span className={styles.subjectName}>{sub.subject_name}</span>
                  {sub.is_verify === 1 ? (
                    <FaCheck className={styles.verifyIconGreen} />
                  ) : (
                    <FaTimes className={styles.verifyIconRed} />
                  )}
                </div>
              ))}
            </div>
          );
        },
      },
      {
        accessorKey: "publish_marks",
        header: t.publishStatus || "Publish Status",
        enableSorting: true,
        enableColumnFilter: false,
        // size: 150,
        Cell: ({ row }) =>
          row.original.publish_marks === 1 ? (
            <span className={styles.publishedBadge}>
              {t.published || "Published"}
            </span>
          ) : (
            <span className={styles.notPublishedBadge}>
              {t.notYetPublished || "Not Yet Published"}
            </span>
          ),
      },
      {
        id: "actions",
        header: t.actions || "Action",
        enableSorting: false,
        enableColumnFilter: false,
        size: 140,
        Cell: ({ row }) => {
          // if (row.original.publish_marks === 1) {
          //   return (
          //     <span className={styles.publishedBadge}>
          //       {t.published || "PUBLISHED"}
          //     </span>
          //   );
          // }

          return (
            <button
              className={styles.viewBtn}
              onClick={() => handlePublishView(row.original)}
              title={t.view || "View"}
            >
              <FaEye />
            </button>
          );
        },
      },
    ],
    [t],
  );

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FaClipboardList />
        </div>
        <h1 className={styles.headerTitle}>
          {t.publishMarkList || "Publish Mark List"}
        </h1>
      </div>

      {/* Filters */}
      <div className={styles.filterSection}>
        <div className="row mb-3 align-items-end">
          {/* Class Dropdown */}
          <div className="col-lg-3 col-md-4 col-sm-6 px-2 mb-2">
            <CustomDropdown
              label={`${t.class || "Class"}*`}
              name="filterClass"
              value={selectedClass}
              onChange={handleClassChange}
              options={classOptions}
              placeholder={t.selectClass || "Select Class"}
              loading={classLoading}
            />
          </div>

          {/* Section Dropdown */}
          <div className="col-lg-3 col-md-4 col-sm-6 px-2 mb-2">
            <CustomDropdown
              label={`${t.section || "Section"}*`}
              name="filterSection"
              value={selectedSection}
              onChange={handleSectionChange}
              options={sectionOptions}
              placeholder={t.selectSection || "Select Section"}
              disabled={!selectedClass}
            />
          </div>

          {/* Exam Name Dropdown */}
          <div className="col-lg-3 col-md-4 col-sm-6 px-2 mb-2">
            <CustomDropdown
              label={t.examName || "Exam Name"}
              name="examName"
              value={selectedExamName}
              onChange={handleExamNameChange}
              options={examNameOptions}
              placeholder={t.selectExamName || "Select Exam Name"}
            />
          </div>

          {/* Search & Reset Buttons */}
          <div className="col-lg-3 col-md-4 col-sm-6 px-2 mb-4 d-flex gap-2 align-items-end">
            <CustomButton
              variant="contained"
              onClick={handleSearch}
              className={styles.addButton}
              loading={searchLoading}
              loadingText="..."
              disabled={!selectedClass || !selectedSection || !selectedExamName}
            >
              {t.searchButton || "SEARCH"}
            </CustomButton>

            <CustomButton
              variant="outlined"
              onClick={handleReset}
              className={styles.cancelButton}
            >
              {t.resetButton || "RESET"}
            </CustomButton>
          </div>
        </div>
      </div>

      {/* Table */}
      <CustomMRTTable
        columns={columns}
        data={paginatedData}
        loading={loading}
        pagination={pagination}
        rowCount={filteredData.length}
        onPaginationChange={setPagination}
        placeholder={translations.labels.searchButton}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        onSearchEnter={(value) => {
          setApiSearch(value);
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        }}
      />
    </div>
  );
};

export default PublishMarkList;
